import { useEffect, useState } from 'react'

/**
 * Reduce-motion: two switches, one behaviour.
 *
 * design-system.md §5: "`prefers-reduced-motion` AND the in-app Reduce Animation
 * setting both collapse springs to opacity fades."
 *
 * 🔴 THIS DOES NOT RETURN A FLAG FOR COMPONENTS TO BRANCH ON.
 * It resolves both switches and stamps `data-reduce-motion="true"` on the
 * document root; `kid.css` already keys off that attribute and the OS media
 * query. The moment a component reads this value, the rule has to be
 * re-implemented in every component and one of them will get it wrong. It is a
 * single CSS concern with a single application point.
 *
 * Not a preference. Vestibular sensitivity is real, and some children find
 * motion distressing (design-system.md §1) — this is an accessibility control.
 *
 * The in-app setting is surfaced on G3 Accessibility (Unit 10); this is where
 * it is *applied*, so every surface honours it before that screen exists.
 */
const STORAGE_KEY = 'kalqy.reduceMotion'

/** Read the persisted in-app setting. Absent means "follow the OS". */
export function getReduceMotionSetting(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true'
  } catch {
    // Private mode, or storage disabled. Fall back to the OS preference.
    return false
  }
}

export function setReduceMotionSetting(on: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(on))
  } catch {
    // Non-fatal: the OS preference still applies.
  }
  applyReduceMotion(on || prefersReducedMotion())
}

function prefersReducedMotion(): boolean {
  return typeof matchMedia === 'function'
    ? matchMedia('(prefers-reduced-motion: reduce)').matches
    : false
}

function applyReduceMotion(on: boolean): void {
  document.documentElement.dataset.reduceMotion = String(on)
}

/**
 * Mount once, near the root. Applies the resolved value and keeps it in step
 * with the OS preference if it changes mid-session.
 */
export function useApplyReduceMotion(): void {
  // Kept only so a settings screen can render the current state; nothing about
  // motion behaviour depends on this value.
  const [, force] = useState(0)

  useEffect(() => {
    const resolve = () => {
      applyReduceMotion(getReduceMotionSetting() || prefersReducedMotion())
      force((n) => n + 1)
    }

    resolve()

    if (typeof matchMedia !== 'function') return
    const mq = matchMedia('(prefers-reduced-motion: reduce)')
    mq.addEventListener('change', resolve)
    return () => mq.removeEventListener('change', resolve)
  }, [])
}
